import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './index.css';
import TextInput from './TextInput.js';
import AudioControls from './AudioControls.js';
import SpeakerSelect from './SpeakerSelect.js';
import TextContent from './TextContent.js';
import ChatGPTPrompt from './ChatGPTPrompt.js';
import HamburgerMenu from './HamburgerMenu.js'; // Import the hamburger menu


const App = () => {
  const [audio, setAudio] = useState(null);
  const [audioReady, setAudioReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [speakerData, setSpeakerData] = useState([]);
  const [translationEnabled, setTranslationEnabled] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("es"); // Default to Spanish
  const [textContent, setTextContent] = useState("Enter your text below to get started.");
  const [textArray, setTextArray] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1); // Track highlighted word
  const [isScrubbing, setIsScrubbing] = useState(false); // Track scrubbing state
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [selectedSpeaker, setSelectedSpeaker] = useState(null);
  const [apiKey, setApiKey] = useState(null); // Initially null
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || "https://wsl-api-demo.onrender.com";
  const audioRef = useRef(null); // Reference for audio element
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  //Ensure useRef exists before initilization 
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = {};  // ✅ Initialize it early
    }
  }, []);  
  
  //Mobile App Settings 
    useEffect(() => {
      const handleResize = () => {
        setIsMobile(window.innerWidth < 768);
      };
  
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);

  //Get API key from render backend
  useEffect(() => {
    fetch(`${API_BASE_URL}/get-api-key`)
        .then(response => response.json())
        .then(data => setApiKey(data.apiKey))
        .catch(error => console.error("Error fetching API key:", error));
}, []);

  //Get WellSaid API key from env variable 
  useEffect(() => {
    fetch('/get-api-key') // Request the key from backend
        .then(response => response.json())
        .then(data => {
            setApiKey(data.apiKey); // Store API key in state
        })
        .catch(error => console.error("Error fetching API key:", error));
}, []);

  // ✅ Ensure `textArray` updates whenever `textContent` changes
  useEffect(() => {
    setTextArray(textContent.split(" ")); 
  }, [textContent]);  

  // Fetch speaker data
  useEffect(() => {
    if (apiKey) {
      axios.get('https://api.wellsaidlabs.com/v1/tts/avatars', {
        headers: {
          "X-API-KEY": apiKey,
          "Accept": "application/json"
        }
      })
        .then((response) => {
          setSpeakerData(response.data.avatars);
        })
        .catch((error) => {
          alert("Failed to load speakers. Check your API key and try again.");
        });
    }
  }, [apiKey]);

  // Set initial speaker
  useEffect(() => {
    if (speakerData.length > 0 && !selectedSpeaker) {
      setSelectedSpeaker(speakerData[20].id); // Automatically select one speaker
    }
  }, [speakerData]);

  // Define a callback for speaker changes triggered by user interaction.
  const handleSpeakerChange = (newSpeaker) => {
    // Directly call sendTextToAPI with sync enabled.
    setSelectedSpeaker(newSpeaker);
    sendTextToAPI(textContent, newSpeaker, true, true);
  };

//Main SEND TEXT TO API FUNCTION
//THIS IS THE BIG ONE TWO COMMENTS TO FIND IT HEHE

const sendTextToAPI = async (text, selectedSpeaker, autoPlay = false, sync = false) => {
  setLoading(true);
  if (!selectedSpeaker) {
      console.error("Speaker ID is missing!");
      return;
  }
    // ✅ Check if existing audio matches the current text and speaker
    if (
      audioRef.current &&
      audioRef.current.audio &&
      audioRef.current.text === text.trim() &&
      audioRef.current.speaker === selectedSpeaker
  ) {
      console.log("🔄 Playing existing audio...");
      audioRef.current.audio.currentTime = 0;
      audioRef.current.audio.play()
          .then(() => setIsAudioPlaying(true))
          .catch(error => console.error("Playback error:", error));
      setLoading(false);
      return;
  }
  
  let finalText = text; // Preserve original text

  // 🔄 **Proceed to WellSaid API with translated or original text**
  let syncTime = 0;
  const oldAudio = audio;
  if (sync && oldAudio) {
      if (!oldAudio.paused && oldAudio.currentTime > 0 && oldAudio.currentTime < oldAudio.duration) {
          syncTime = oldAudio.currentTime; // Preserve playback position
      }
  }

  if (oldAudio) {
      oldAudio.pause();
      setIsAudioPlaying(false);
  }
        //set library only if english voice
        try {
          // ✅ Create request body
          const requestBody = {
              speaker_id: selectedSpeaker,
              text,
          };

          // ✅ Include `library_ids` **ONLY IF** speaker ID is LESS than 200
          if (parseInt(selectedSpeaker, 10) < 200) {
              requestBody.library_ids = ["c1b7c009-904d-48c2-a2d6-4249863cb995"];
          }

          const response = await axios.post(
              'https://api.wellsaidlabs.com/v1/tts/stream',
              requestBody,
          {
              headers: {
                  'X-API-KEY': apiKey,
                  'Content-Type': 'application/json',
                  'Accept': 'application/json'
              },
              responseType: 'blob'
          }
      );

      const audioBlob = new Blob([response.data], { type: 'audio/mpeg' });
      const newAudio = new Audio(URL.createObjectURL(audioBlob));

      audioRef.current = {
          audio: newAudio,
          text: finalText.trim(),  // ✅ Ensure trimmed comparison
          speaker: selectedSpeaker // ✅ Store selected speaker
      };

      setAudio(newAudio);

      newAudio.addEventListener('loadedmetadata', () => {
          if (sync && syncTime > 0 && syncTime < newAudio.duration) {
              newAudio.currentTime = syncTime; // ✅ Resume from last position
          }
      });

      newAudio.addEventListener('canplaythrough', () => {
          setAudioReady(true);
          if (autoPlay) {
              newAudio.play()
                  .then(() => setIsAudioPlaying(true))
                  .catch((error) => console.error("Error auto-playing audio:", error));
          } else {
              setIsAudioPlaying(false);
          }
          setLoading(false);
      });

      newAudio.onplay = () => setIsAudioPlaying(true);
      newAudio.onpause = () => {
          setIsAudioPlaying(false);
          setIsScrubbing(false);
      };
      newAudio.onended = () => setIsAudioPlaying(false);
  } catch (error) {
      console.error("🚨 Error fetching audio:", error);
      setLoading(false);

      if (!error.response) {
          alert("⚠️ An unknown error occurred. Please check your input and try again.");
          return;
      }

      try {
          // Convert Blob to JSON
          const errorData = await error.response.data.text();
          const { code, message } = JSON.parse(errorData);
  
          console.log(`📌 Error Code: ${code} | Message: ${message}`);
  
          alert(code === "CONTENT_NOT_ALLOWED"
              ? "⚠️ Bingham, please stop putting curse words into my app - Love Nate."
              : `⚠️ Error: ${message || "An issue occurred while generating audio."}`
          );
      } catch (parseError) {
          console.error("🚨 Failed to parse error response:", parseError);
          alert("⚠️ An unknown error occurred. Please check your input and try again.");
      }
  }
};

  // Highlight text based on audio time
  const highlightText = () => {
    if (audio && audio.currentTime !== undefined && audio.duration > 0) {
      const wordCount = textArray.length;
      const wordIndex = Math.floor((audio.currentTime / audio.duration) * wordCount);
      if (highlightedIndex !== wordIndex) {
        setHighlightedIndex(wordIndex);
      }
    }
  };

  // Handle slider changes to scrub audio
  const handleSliderChange = (event) => {
    if (audio && audio.currentTime !== undefined) {
      const newTime = event.target.value;
      audio.currentTime = newTime;
      const wordCount = textArray.length;
      const wordIndex = Math.floor((audio.currentTime / audio.duration) * wordCount);
      setHighlightedIndex(wordIndex);
      setIsScrubbing(true);
    }
  };

  const handleSliderChangeEnd = () => {
    setIsScrubbing(false);
  };

  // Sync highlighting while audio is playing
  useEffect(() => {
    if (audio) {
      const interval = setInterval(() => {
        if (audio && audio.currentTime !== undefined) {
          const wordCount = textArray.length;
          const wordIndex = Math.floor((audio.currentTime / audio.duration) * wordCount);
          setHighlightedIndex(wordIndex);
        }
      }, 100);
      return () => clearInterval(interval);
    }
  }, [audio, isScrubbing]);

  const handleGenerateText = (prompt) => {
    console.log("Generating text for prompt:", prompt);
    // Placeholder for future API integration with ChatGPT
};

  return (
    <div className="container">
      <header className="app-header">
        <div className="header-left">
          <HamburgerMenu />
        </div>
        <div className="header-center">
          <h1>{isMobile ? "WellSaid Mobile Demo" : "WellSaid API Demo"}</h1>
        </div>
        <div className="header-right">
          {/* This empty div acts as a placeholder */}
        </div>
      </header>

      <TextContent 
        textContent={textContent}
        highlightedIndex={highlightedIndex} 
        isAudioPlaying={isAudioPlaying} 
        isScrubbing={isScrubbing} 
      />
      <AudioControls
        sendTextToAPI={sendTextToAPI}
        audio={audio}
        loading={loading}
        setLoading={setLoading}
        handleSliderChange={handleSliderChange}
        handleSliderChangeEnd={handleSliderChangeEnd}
        isAudioPlaying={isAudioPlaying}  // ✅ Pass the state
        setIsAudioPlaying={setIsAudioPlaying}  // ✅ Pass the setter function
      />
      <SpeakerSelect 
          speakerData={speakerData} 
          selectedSpeaker={selectedSpeaker} 
          setSelectedSpeaker={setSelectedSpeaker}
          sendTextToAPI={sendTextToAPI}  // ✅ Pass function
          textContent={textContent}      // ✅ Pass text state
          audioRef={audioRef}            // ✅ Pass audio reference
      />
      <TextInput 
          sendTextToAPI={sendTextToAPI} 
          setTextContent={setTextContent} 
          selectedSpeaker={selectedSpeaker} 
          setSelectedSpeaker={setSelectedSpeaker}
          textContent={textContent} 
          translationEnabled={translationEnabled} 
          setTranslationEnabled={setTranslationEnabled}  // ✅ Pass setter
          selectedLanguage={selectedLanguage}
          setSelectedLanguage={setSelectedLanguage}  // ✅ Pass setter
          audioRef={audioRef}  
      />
      <ChatGPTPrompt 
        setTextContent={setTextContent}
        sendTextToAPI={sendTextToAPI} 
        selectedSpeaker={selectedSpeaker}
      />
    </div>
  );
};

export default App;